/**
 * Survey progress estimation.
 *
 * A survey's question array is the WORST case, not what a respondent actually answers: conditional
 * display rules, block gating and skip logic mean most of a 92-question survey is never shown. Telling
 * a respondent "5 / 92" is both wrong and scary. Instead we estimate the questions THIS respondent
 * will actually face and report progress as a percentage:
 *
 *   - Before a branch is decided, mutually-exclusive blocks (gated on the same earlier question, e.g.
 *     six category deep-dives) collapse to their LONGEST member — the longest branch a respondent
 *     could land on. Definitely-hidden questions are excluded.
 *   - Once the respondent answers the deciding question, the estimate re-resolves to the branch they
 *     actually took (usually shrinking the total).
 *   - Adaptive follow-ups aren't in the authored array, so each follow-up asked adds 1 to BOTH the
 *     total and the attempted count — the bar reflects the extra question instead of overshooting.
 *
 * The backend is authoritative for what is actually shown; this only drives the progress UI.
 */

export type ConditionState = 'true' | 'false' | 'undecided';

interface Condition {
  source_question_id?: string;
  operator?: string;
  value?: unknown;
  option_key?: string;
  threshold?: number;
}

interface DisplayCondition {
  logic?: 'and' | 'or';
  conditions?: Condition[];
  source_question_id?: string;
  operator?: string;
  value?: unknown;
}

interface ResponseItem {
  type?: string;
  value?: unknown;
}
export interface SessionResponse {
  question_id: string;
  response_data?: ResponseItem[];
  state?: string;
}

export interface ProgressQuestion {
  id: string;
  block_id?: string | null;
  display_condition?: DisplayCondition | null;
  is_screener?: boolean;
}

export interface ProgressBlock {
  id: string;
  display_condition?: DisplayCondition | null;
}

const CHOICE_TYPES = [
  'single_choice',
  'multiple_choice',
  'yes_no',
  'dropdown',
  'original_response',
];
const NUMERIC_OPS = ['>', '>=', '=', '<', '<='];

function selectedValues(resp: SessionResponse | undefined): string[] | null {
  if (!resp || !Array.isArray(resp.response_data)) return null;
  const vals: string[] = [];
  for (const item of resp.response_data) {
    if (item.type && CHOICE_TYPES.includes(item.type)) vals.push(String(item.value));
  }
  return vals;
}

function evalSingle(cond: Condition, byId: Map<string, SessionResponse>): ConditionState {
  const src = cond.source_question_id ? byId.get(cond.source_question_id) : undefined;
  // Source not answered yet → we can't decide this branch.
  if (!src || src.state === 'conditionally_skipped' || !src.response_data?.length)
    return 'undecided';

  if (cond.operator && NUMERIC_OPS.includes(cond.operator)) {
    const original = src.response_data?.find((i) => i.type === 'original_response');
    const alloc = original?.value as Record<string, number> | undefined;
    if (!alloc || typeof alloc !== 'object') return 'undecided';
    const v = alloc[cond.option_key ?? ''];
    if (v === undefined) return 'true'; // option absent → shown by default (mirrors runtime)
    const t = cond.threshold ?? 0;
    const ok =
      cond.operator === '>'
        ? v > t
        : cond.operator === '>='
          ? v >= t
          : cond.operator === '='
            ? v === t
            : cond.operator === '<'
              ? v < t
              : v <= t;
    return ok ? 'true' : 'false';
  }

  const sel = selectedValues(src);
  if (sel === null) return 'undecided';
  const has = sel.includes(String(cond.value));
  if (cond.operator === 'selected') return has ? 'true' : 'false';
  if (cond.operator === 'not_selected') return has ? 'false' : 'true';
  return 'true';
}

/** Evaluate a display condition to true / false / undecided against the answers so far. */
export function evalDisplayCondition(
  dc: DisplayCondition | null | undefined,
  byId: Map<string, SessionResponse>,
): ConditionState {
  if (!dc) return 'true';
  const list =
    dc.conditions && dc.conditions.length
      ? dc.conditions
      : dc.source_question_id
        ? [dc as Condition]
        : [];
  if (!list.length) return 'true';

  const states = list.map((c) => evalSingle(c, byId));
  const logic = dc.logic || 'and';
  if (logic === 'or') {
    if (states.includes('true')) return 'true';
    if (states.includes('undecided')) return 'undecided';
    return 'false';
  }
  if (states.includes('false')) return 'false';
  if (states.includes('undecided')) return 'undecided';
  return 'true';
}

function sourceOf(dc: DisplayCondition | null | undefined): string | null {
  if (!dc) return null;
  if (dc.conditions?.length) return dc.conditions[0].source_question_id ?? null;
  return dc.source_question_id ?? null;
}

/**
 * Estimate how many authored questions THIS respondent will actually be shown, given the answers so
 * far. Mutually-exclusive blocks that are still undecided collapse to their longest member.
 * Screener questions are excluded — the progress bar is hidden on them.
 */
export function estimateReachableTotal(
  questions: ProgressQuestion[],
  blocks: ProgressBlock[] | null | undefined,
  responses: SessionResponse[] | null | undefined,
): number {
  if (!Array.isArray(questions) || questions.length === 0) return 0;
  const byId = new Map<string, SessionResponse>();
  for (const r of responses ?? []) byId.set(r.question_id, r);

  const blockById = new Map<string, ProgressBlock>();
  for (const b of blocks ?? []) blockById.set(b.id, b);

  // Questions per block (after excluding screeners), and group blocks that gate on the same source.
  const blockLen = new Map<string, number>();
  for (const q of questions) {
    if (q.is_screener) continue;
    const bid = q.block_id || null;
    if (bid) blockLen.set(bid, (blockLen.get(bid) ?? 0) + 1);
  }
  const groupBySource = new Map<string, string[]>(); // source qid -> block ids gated on it
  for (const b of blocks ?? []) {
    const src = sourceOf(b.display_condition);
    if (!src) continue;
    const arr = groupBySource.get(src) ?? [];
    if (!arr.includes(b.id)) arr.push(b.id);
    groupBySource.set(src, arr);
  }
  // For each mutually-exclusive group, the single longest block (the "longest branch").
  const longestInGroup = new Map<string, string>();
  for (const [src, ids] of groupBySource) {
    if (ids.length <= 1) continue;
    let best = ids[0];
    for (const id of ids) if ((blockLen.get(id) ?? 0) > (blockLen.get(best) ?? 0)) best = id;
    longestInGroup.set(src, best);
  }

  let total = 0;
  for (const q of questions) {
    if (q.is_screener) continue;
    const bid = q.block_id || null;
    if (bid) {
      const block = blockById.get(bid);
      const bstate = evalDisplayCondition(block?.display_condition, byId);
      if (bstate === 'false') continue;
      if (bstate === 'undecided') {
        const src = sourceOf(block?.display_condition);
        const group = src ? groupBySource.get(src) : undefined;
        // Undecided mutually-exclusive group → only the longest member counts toward the estimate.
        if (src && group && group.length > 1 && longestInGroup.get(src) !== bid) continue;
      }
    }
    if (evalDisplayCondition(q.display_condition, byId) === 'false') continue;
    total += 1;
  }
  return total;
}

export interface ProgressInput {
  questions: ProgressQuestion[];
  blocks?: ProgressBlock[] | null;
  responses?: SessionResponse[] | null;
  /** Follow-ups already answered this session (each was an extra, uncounted question). */
  followupsAnswered?: number;
  /** A follow-up is currently on screen (counts toward the total but not yet attempted). */
  followupActive?: boolean;
}

export interface SurveyProgress {
  attempted: number;
  total: number;
  percent: number;
}

/**
 * Progress as attempted / total with follow-ups folded in, plus a clamped percentage for the bar.
 * `attempted` = answered base questions + follow-ups answered. `total` = estimated reachable base
 * questions + follow-ups asked (answered + the active one). Percent never shows 100 until the
 * respondent is genuinely done (attempted >= total).
 */
export function computeSurveyProgress(input: ProgressInput): SurveyProgress {
  const { questions, blocks, responses, followupsAnswered = 0, followupActive = false } = input;
  const answeredBase = (responses ?? []).filter((r) => r.state === 'answered').length;
  const reachableBase = estimateReachableTotal(questions, blocks, responses);

  const attempted = answeredBase + followupsAnswered;
  const followupsAsked = followupsAnswered + (followupActive ? 1 : 0);
  // Total can never be below what's already attempted (estimates only ever grow toward reality).
  const total = Math.max(reachableBase + followupsAsked, attempted, 1);

  const percent = Math.min(100, Math.max(0, Math.round((attempted / total) * 100)));
  return { attempted, total, percent };
}
